import { Button, Col, Descriptions, Radio, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import {
  getTripTrackingInformation,
  getUserRequestById,
} from "../../services/userRequestApi";
import { TRIP_STATUS } from "../../shared/constants/TripStatus";
import { WebRoutes } from "../../shared/constants/WebRoutes";
import UserRequestDTO from "../../shared/dtos/userRequestDTO";
import { destinationLabel } from "../../utils/helpers/destinationLabel";
import ContainerLayout from "../common/containerLayout";
import GroupTripsMap from "../groupTrip/groupTripsMap/groupTripsMap";
import { checkPointsDTO } from "../../shared/dtos/checkPointsDTO";
import { LocationDTO } from "../../shared/dtos/PassengerGroupDTO";

const { Title } = Typography;
type PathViewMode = "clean" | "raw";

const formatDistanceKm = (value?: number | null) => {
  if (!Number.isFinite(value)) return "--";

  return `${new Intl.NumberFormat("es-UY", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number(value))} km`;
};

const formatMinutes = (value?: number | null) => {
  if (!Number.isFinite(value)) return "--";

  return `${new Intl.NumberFormat("es-UY", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Number(value))} min`;
};

const AdminPanelTripDetails = (): JSX.Element => {
  let { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<UserRequestDTO | null>(null);
  const [tripCoordinates, setTripCoordinates] = useState<LocationDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pathViewMode, setPathViewMode] = useState<PathViewMode>("clean");
  const officialDurationMinutes = trip?.payment?.minutes ?? null;

  useEffect(() => {
    const getTripCoordinates = async () => {
      if (!id) {
        if (state.navigationType === "Admin") {
          navigate(WebRoutes.IndexAdminPanel, {
            replace: true,
          });
        } else if (state.navigationType === "SuperAdmin") {
          navigate(WebRoutes.TripTableRoute, {
            replace: true,
          });
        }
        return;
      }

      const tripDetails = await getUserRequestById({ userRequestId: +id });
      setTrip(tripDetails);
      if (tripDetails.status === TRIP_STATUS.Completed) {
        try {
          const tripDetailedCoordinates: LocationDTO[] =
            await getTripTrackingInformation(tripDetails.bookingId);
          setTripCoordinates(tripDetailedCoordinates);
        } catch (e) {
          setError(true);
        }
      }
      setLoading(false);
    };

    getTripCoordinates();
  }, [id, navigate, state.navigationType]);

  const handleOnClickReturn = () => {
    if (state.navigationType === "Admin") {
      navigate(WebRoutes.IndexAdminPanel, {
        replace: true,
      });
    } else if (state.navigationType === "SuperAdmin") {
      navigate(WebRoutes.TripTableRoute, {
        replace: true,
      });
    }
  };

  return (
    <ContainerLayout title={state.bookingId} showNotification={false}>
      <Col span={12}>
        <Descriptions
          title="Detalles del viaje"
          column={1}
          extra={
            <Button type="link" onClick={handleOnClickReturn}>
              <Space>
                <ArrowLeftOutlined />
                Regresar
              </Space>
            </Button>
          }
          labelStyle={{ fontWeight: "bold" }}
        >
          <Descriptions.Item label="Cliente">
            {state.customerName}
          </Descriptions.Item>
          <Descriptions.Item label="Conductor">
            {state.providerName}
          </Descriptions.Item>
          <Descriptions.Item label="Departamento">
            {state.department}
          </Descriptions.Item>
          <Descriptions.Item label="Distancia">
            {formatDistanceKm(state.distance)}
          </Descriptions.Item>
          <Descriptions.Item label="Distancia estimada">
            {formatDistanceKm(trip?.payment?.estimatedDistance)}
          </Descriptions.Item>
          <Descriptions.Item label="Duración">
            {formatMinutes(officialDurationMinutes)}
          </Descriptions.Item>
          <Descriptions.Item label="Duración estimada">
            {formatMinutes(trip?.payment?.estimatedMinutes)}
          </Descriptions.Item>
          <Descriptions.Item label="Hora inicio viaje">
            {state.startedAt
              ? dayjs(state.startedAt, "YYYY-MM-DD HH:mm:ss").format(
                "DD-MM-YYYY HH:mm",
              )
              : ""}
          </Descriptions.Item>
          <Descriptions.Item label="Hora final viaje">
            {state.finishedAt
              ? dayjs(state.finishedAt, "YYYY-MM-DD HH:mm:ss").format(
                "DD-MM-YYYY HH:mm",
              )
              : ""}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección recogida">
            {state?.originAddress}
          </Descriptions.Item>
          {trip?.destinations
            ?.sort(
              (x: checkPointsDTO, y: checkPointsDTO) =>
                Number(x.id) - Number(y.id),
            )
            .map((destinations: any, index: number) => (
              <Descriptions.Item
                label={destinationLabel(index + 1, trip.destinations?.length!)}
              >
                {destinations.address}
              </Descriptions.Item>
            ))}
          <Descriptions.Item label="Cantidad total">
            {state.formattedTotal}
            {state.currency ? ` (${state.currency})` : ""}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo de servicio">
            {trip?.serviceType?.name ?? "--"}
          </Descriptions.Item>
          <Descriptions.Item label="Nombre pasajero">
            {trip?.passengersData?.[0]?.name ??
              (trip?.user?.firstName ?? "") +
              " " +
              (trip?.user?.lastName ?? "")}
          </Descriptions.Item>
          <Descriptions.Item label="Teléfono pasajero">
            {trip?.passengersData?.[0]?.phone ?? trip?.user?.mobile ?? "--"}
          </Descriptions.Item>
        </Descriptions>
      </Col>
      <Col
        span={12}
        className={loading || error ? "loading-container error-container" : undefined}
      >
        {loading && (
          <>
            <LoadingOutlined />
            <Title level={2}>Generando detalles del viaje</Title>
          </>
        )}

        {!loading && !error && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {tripCoordinates.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", minHeight: 32 }}>
                <Radio.Group
                  value={pathViewMode}
                  onChange={(event) => setPathViewMode(event.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="clean">Ruta limpia</Radio.Button>
                  <Radio.Button value="raw">GPS real</Radio.Button>
                </Radio.Group>
              </div>
            )}

            <GroupTripsMap
              type="tripDetails"
              trip={tripCoordinates}
              originAddress={state.originAddress!}
              originLat={trip?.originLatitude!}
              originLng={trip?.originLongitude!}
              destinationAddress={trip?.destinationAddress!}
              destinationLat={trip?.destinationLatitude!}
              destinationLng={trip?.destinationLongitude!}
              waypoints={trip?.destinations!}
              margin="0"
              height="85vh"
              pathViewMode={pathViewMode}
            />
          </div>
        )}

        {!loading && error && (
          <>
            <ExclamationCircleOutlined />
            <Title level={2}>No hay datos</Title>
          </>
        )}
      </Col>
    </ContainerLayout>
  );
};

export default AdminPanelTripDetails;
